import { useEffect, useState } from "react";
import { Save, Plus, X } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { adminGetSettings, adminPutSettings } from "@/lib/api";
import MarketingLinksCard from "@/components/admin/MarketingLinksCard";
import { toast } from "sonner";

const WEEKDAYS = [
  { i: 0, label: "Mon" },
  { i: 1, label: "Tue" },
  { i: 2, label: "Wed" },
  { i: 3, label: "Thu" },
  { i: 4, label: "Fri" },
  { i: 5, label: "Sat" },
  { i: 6, label: "Sun" },
];

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const s = await adminGetSettings();
        setForm(s);
      } catch {
        toast.error("Could not load settings");
      }
    })();
  }, []);

  function up(patch) {
    setForm((f) => ({ ...f, ...patch }));
  }

  function toggleWeekday(i) {
    const set = new Set(form.recurring_blocked_weekdays);
    if (set.has(i)) set.delete(i);
    else set.add(i);
    up({ recurring_blocked_weekdays: Array.from(set).sort((a, b) => a - b) });
  }

  function addOneOffDate() {
    if (!newDate) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
      toast.error("Use YYYY-MM-DD");
      return;
    }
    if (form.one_off_blocked_dates.includes(newDate)) {
      setNewDate("");
      return;
    }
    up({
      one_off_blocked_dates: [...form.one_off_blocked_dates, newDate].sort(),
    });
    setNewDate("");
  }

  function removeOneOffDate(d) {
    up({
      one_off_blocked_dates: form.one_off_blocked_dates.filter((x) => x !== d),
    });
  }

  async function onSave(e) {
    e.preventDefault();
    if (!form || saving) return;
    setSaving(true);
    try {
      const payload = {
        hourly_rate: Number(form.hourly_rate),
        minimum_charge: Number(form.minimum_charge),
        hours: {
          sidewalk: Number(form.hours.sidewalk),
          deck: Number(form.hours.deck),
          furniture: Number(form.hours.furniture),
          other: Number(form.hours.other),
        },
        recurring_blocked_weekdays: form.recurring_blocked_weekdays,
        one_off_blocked_dates: form.one_off_blocked_dates,
        google_review_url: (form.google_review_url || "").trim(),
        notification_email: (form.notification_email || "").trim(),
        marketing_channels: Array.isArray(form.marketing_channels)
          ? form.marketing_channels
          : [],
      };
      const saved = await adminPutSettings(payload);
      setForm(saved);
      toast.success("Settings saved");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!form) {
    return (
      <AdminShell>
        <div className="py-16 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <form onSubmit={onSave} className="pt-2 space-y-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#334E68]">
            Settings
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            These control the public estimate + calendar in real-time.
          </p>
        </div>

        {/* Pricing */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
          <h2 className="text-xs uppercase tracking-wider font-semibold text-gray-500">
            Pricing
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#334E68]">Hourly rate ($)</Label>
              <Input
                data-testid="settings-hourly-rate"
                type="number"
                min={1}
                value={form.hourly_rate}
                onChange={(e) => up({ hourly_rate: e.target.value })}
                className="mt-1 min-h-[52px] rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm text-[#334E68]">Minimum ($)</Label>
              <Input
                data-testid="settings-minimum"
                type="number"
                min={0}
                value={form.minimum_charge}
                onChange={(e) => up({ minimum_charge: e.target.value })}
                className="mt-1 min-h-[52px] rounded-xl"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">
              Hours per scope item
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {["sidewalk", "deck", "furniture", "other"].map((k) => (
                <div key={k}>
                  <Label className="text-sm text-[#334E68] capitalize">{k}</Label>
                  <Input
                    data-testid={`settings-hours-${k}`}
                    type="number"
                    min={0}
                    step="0.25"
                    value={form.hours[k]}
                    onChange={(e) =>
                      up({ hours: { ...form.hours, [k]: e.target.value } })
                    }
                    className="mt-1 min-h-[52px] rounded-xl"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Availability */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
          <h2 className="text-xs uppercase tracking-wider font-semibold text-gray-500">
            Availability
          </h2>

          <div className="mt-3">
            <Label className="text-sm text-[#334E68]">
              Recurring blocked weekdays
            </Label>
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2">
              {WEEKDAYS.map((d) => {
                const checked = form.recurring_blocked_weekdays.includes(d.i);
                return (
                  <label
                    key={d.i}
                    data-testid={`settings-weekday-${d.i}`}
                    className={[
                      "min-h-[52px] px-3 rounded-xl border-2 flex items-center gap-2 cursor-pointer transition-colors",
                      checked
                        ? "border-[#16A34A] bg-green-50/50"
                        : "border-gray-200 bg-white",
                    ].join(" ")}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleWeekday(d.i)}
                      className="w-5 h-5 border-2 data-[state=checked]:bg-[#16A34A] data-[state=checked]:border-[#16A34A]"
                    />
                    <span className="text-sm font-medium text-[#334E68]">
                      {d.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <Label className="text-sm text-[#334E68]">
              One-off blocked dates
            </Label>
            <div className="mt-2 flex gap-2">
              <Input
                data-testid="settings-new-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="min-h-[52px] rounded-xl flex-1"
              />
              <button
                type="button"
                data-testid="settings-add-date"
                onClick={addOneOffDate}
                className="min-h-[52px] px-4 rounded-xl bg-[#334E68] text-white text-sm font-semibold uppercase tracking-wide flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {form.one_off_blocked_dates.length === 0 && (
                <span className="text-xs text-gray-500">
                  None. Add ad-hoc unavailable days above.
                </span>
              )}
              {form.one_off_blocked_dates.map((d) => (
                <span
                  key={d}
                  data-testid={`settings-oneoff-${d}`}
                  className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-gray-100 text-sm text-[#334E68]"
                >
                  {d}
                  <button
                    type="button"
                    aria-label={`Remove ${d}`}
                    data-testid={`settings-oneoff-remove-${d}`}
                    onClick={() => removeOneOffDate(d)}
                    className="w-6 h-6 rounded-full hover:bg-gray-200 flex items-center justify-center"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Notifications & links */}
        <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
          <h2 className="text-xs uppercase tracking-wider font-semibold text-gray-500">
            Notifications &amp; links
          </h2>
          <div className="mt-3 space-y-4">
            <div>
              <Label className="text-sm text-[#334E68]">Notification email</Label>
              <Input
                data-testid="settings-notification-email"
                type="email"
                value={form.notification_email || ""}
                onChange={(e) => up({ notification_email: e.target.value })}
                placeholder="you@example.com"
                className="mt-1 min-h-[52px] rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">
                Where new-lead emails go. Falls back to the NOTIFY_EMAIL env var
                if blank.
              </p>
            </div>
            <div>
              <Label className="text-sm text-[#334E68]">Google review URL</Label>
              <Input
                data-testid="settings-review-url"
                type="url"
                value={form.google_review_url || ""}
                onChange={(e) => up({ google_review_url: e.target.value })}
                placeholder="https://g.page/r/..."
                className="mt-1 min-h-[52px] rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used in the &ldquo;Send Review Request&rdquo; text.
              </p>
            </div>
          </div>
        </section>

        <MarketingLinksCard
          channels={form.marketing_channels || []}
          onChange={(list) => up({ marketing_channels: list })}
        />

        <div className="sticky bottom-16 z-30">
          <button
            type="submit"
            data-testid="settings-save"
            disabled={saving}
            className="w-full min-h-[56px] bg-[#16A34A] hover:bg-[#15803D] disabled:bg-gray-300 text-white rounded-2xl font-semibold text-base tracking-wide uppercase flex items-center justify-center gap-2 shadow-lg"
          >
            {saving ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5" /> Save settings
              </>
            )}
          </button>
        </div>
      </form>
    </AdminShell>
  );
}
